import React, { useEffect, useRef } from 'react';
import { IconX } from '@spotify-internal/encore-web';

import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import useCloseOnEsc from 'experiments/feedback-position/hooks/useCloseOnEsc';
import useCloseOnOutsideTouch from 'experiments/feedback-position/hooks/useCloseOnOutsideTouch';

import useShowWithTransition from '../../hooks/useShowWithTransition';
import useDialogFocus from '../../hooks/useDialogFocus';

import FeedbackContent from './FeedbackContent';
import { StyledContainer, StyledDialog, StyledCloseButton } from './styles';

interface FeedbackDialogProps {
  ubi: UbiArticlePage;
  articleIdentifier: string;
  onClose: () => void;
}

const FeedbackDialog = ({ ubi, articleIdentifier, onClose }: FeedbackDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // trap keyboard navigation on dialog
  useDialogFocus({ dialogRef });

  // call on close with delay to display transition
  const { isShow, hideWithTransition } = useShowWithTransition({ delay: 400, onHide: onClose });

  // close dialog when type esc (desktop)
  useCloseOnEsc({ onClose: hideWithTransition });

  // close dialog when touched outside (mobile)
  useCloseOnOutsideTouch({ onClose: hideWithTransition, dialogRef });

  useEffect(() => {
    ubi.logArticleHelpfulImpression();
  }, [ubi]);

  return (
    <StyledContainer data-testid="feedback-dialog-container" aria-modal="true" ref={dialogRef} isShow={isShow}>
      <StyledDialog>
        <FeedbackContent ubi={ubi} articleIdentifier={articleIdentifier} onClose={hideWithTransition} />
        <StyledCloseButton buttonSize="sm" condensed aria-label="close dialog" onClick={hideWithTransition}>
          <IconX iconSize={16} />
        </StyledCloseButton>
      </StyledDialog>
    </StyledContainer>
  );
};

export default FeedbackDialog;
